import { useState, useEffect, useCallback } from 'react';
import { AppProvider, useApp } from './contexts/AppContext';
import { getInitialLang } from './utils/i18n';
import ErrorBoundary from './components/ErrorBoundary';
import SpecificInvites from './components/SpecificInvites';
import Navbar from './components/Navbar';
import Landing from './pages/Landing';
import { FriendRating } from './pages/FriendRating';
import Auth from './pages/Auth';
import Discover from './pages/Discover';
import Matches from './pages/Matches';
import Messages from './pages/Messages';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import BeeHive from './pages/BeeHive';
import StaticPage from './pages/StaticPage';
import Admin from './pages/Admin';
import Agenda from './pages/Agenda';
import Upgrade from './pages/Upgrade';
import BeeWise from './pages/BeeWise';
import './styles/global.css';
import CMLDashboard from './pages/CMLDashboard';
import CmlLeaderboard from './pages/CmlLeaderboard';
import Scout from './pages/Scout';
import BeeConnect from './pages/BeeConnect';

const loadStr = (key, def) => { try { return localStorage.getItem(key) || def; } catch { return def; } };
const saveStr = (key, val) => { try { localStorage.setItem(key, val); } catch {} };

// ── Design tokens (shared with overlays) ─────────────────────────────────
const Gx = {
  bg:'#0A0A0A', card:'#161616', border:'rgba(255,255,255,0.08)',
  white:'#F2E8D0', muted:'#888', gold:'#C9A84C',
  goldPale:'rgba(201,168,76,0.12)', goldBdr:'rgba(201,168,76,0.3)',
  green:'#4EC994', serif:"'Cormorant Garamond',Georgia,serif",
  sans:"'Outfit',system-ui,sans-serif",
};

// ── Post-onboarding interstitiel ─────────────────────────────────────────
// Shown once, first time a user completes onboarding.
// Explains the contract: BeeLove works when friends describe you.
function OnboardingInterstitiel({ onDone }) {
  const [userId,    setUserId]    = useState(null);
  const [userName,  setUserName]  = useState('');
  const [copied,    setCopied]    = useState(false);
  const [committed, setCommitted] = useState(false);
  const [allInvited, setAllInvited] = useState(false);
  const { features, currentUser } = useApp();
  const useSpecific = features?.feature_specific_invites === true;
  const lang = getInitialLang();
  const fr   = lang === 'fr';

  useEffect(() => {
    import('./utils/supabase').then(({ supabase }) => {
      supabase?.auth?.getSession?.().then(({ data }) => {
        if (data?.session?.user?.id) setUserId(data.session.user.id);
        if (data?.session?.user?.user_metadata?.name) setUserName(data.session.user.user_metadata.name);
      });
    });
  }, []);

  useEffect(() => {
    if (currentUser?.name) setUserName(currentUser.name);
  }, [currentUser?.name]);

  const link = userId ? `${window.location.origin}/rate/${userId}` : '';

  const handleCopy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleNativeShare = () => {
    if (!link) return;
    navigator.share?.({ title: 'BeeLove — aide-moi à trouver quelqu\'un', url: link })
      .catch(() => handleCopy());
  };

  const handleDone = async () => {
    saveStr('beelove_contract_shown', '1');
    // Record founding commitment in DB (best-effort, non-blocking)
    if (committed && userId) {
      try {
        const { supabase } = await import('./utils/supabase');
        supabase?.from('user_core')
          .update({ founding_commit_at: new Date().toISOString() })
          .eq('id', userId);
      } catch { /* silent */ }
    }
    onDone();
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(10,10,10,0.97)',
      zIndex:1000, display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', padding:24, fontFamily:Gx.sans,
    }}>
      <div style={{ maxWidth:420, width:'100%' }}>
        <div style={{ fontFamily:Gx.serif, fontSize:48, color:Gx.gold, textAlign:'center', lineHeight:1, marginBottom:8 }}>
          ✦
        </div>
        <div style={{ textAlign:'center', marginBottom:12 }}>
          <span style={{ display:'inline-block', fontSize:10, letterSpacing:'0.14em', textTransform:'uppercase', color:Gx.gold, background:Gx.goldPale, border:`1px solid ${Gx.goldBdr}`, borderRadius:99, padding:'4px 12px' }}>
            {fr ? 'Founding Member' : 'Founding Member'}
          </span>
        </div>
        <h2 style={{ fontFamily:Gx.serif, fontSize:24, fontWeight:400, color:Gx.white, textAlign:'center', marginBottom:10, lineHeight:1.3 }}>
          {fr ? 'Bienvenue dans la Ruche.' : 'Welcome to the Hive.'}
        </h2>
        <p style={{ fontSize:13, color:Gx.muted, lineHeight:1.75, marginBottom:18, textAlign:'center' }}>
          {fr
            ? 'BeeLove fonctionne grâce à tes amis. Ils répondent à 10 questions sur toi, et ton profil de compatibilité devient réel — plus honnête que tout ce que tu pourrais écrire toi-même.'
            : 'BeeLove runs on your friends. They answer 10 questions about you, and your compatibility profile becomes real — more honest than anything you could write yourself.'}
        </p>

        {/* The pact — what it takes */}
        <div style={{ background:Gx.card, border:`1px solid ${Gx.goldBdr}`, borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
          <p style={{ fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:Gx.gold, margin:'0 0 10px' }}>
            {fr ? 'Le pacte Founding Member' : 'The Founding Member pact'}
          </p>
          {[
            { icon:'✦', fr:"J'envoie mon lien à au moins 5 amis cette semaine",    en:'I send my link to at least 5 friends this week' },
            { icon:'✦', fr:'Je réponds honnêtement au quiz pour mes amis qui me le demandent', en:'I answer honestly when friends ask me to rate them' },
            { icon:'✦', fr:"Je reste actif·ve — la communauté se construit ensemble", en:"I stay active — the community is built together" },
          ].map((b, i) => (
            <div key={i} style={{ display:'flex', gap:10, alignItems:'flex-start', marginBottom: i < 2 ? 10 : 0 }}>
              <span style={{ fontSize:15, flexShrink:0 }}>{b.icon}</span>
              <span style={{ fontSize:12, color:Gx.white, lineHeight:1.5 }}>{fr ? b.fr : b.en}</span>
            </div>
          ))}
        </div>

        {/* Explicit commitment */}
        <label style={{
          display:'flex', gap:10, alignItems:'flex-start', padding:'12px 14px', marginBottom:14,
          background: committed ? 'rgba(201,168,76,0.08)' : 'transparent',
          border: `1px solid ${committed ? Gx.goldBdr : Gx.border}`, borderRadius:8, cursor:'pointer',
          transition:'all 0.15s',
        }}>
          <input
            type="checkbox"
            checked={committed}
            onChange={e => setCommitted(e.target.checked)}
            style={{ marginTop:2, accentColor:Gx.gold, width:16, height:16, flexShrink:0, cursor:'pointer' }}
          />
          <span style={{ fontSize:12, color: committed ? Gx.white : Gx.muted, lineHeight:1.5 }}>
            {fr
              ? "Je m'engage comme Founding Member et je partagerai mon lien avec mes amis."
              : "I commit as a Founding Member and will share my link with my friends."}
          </span>
        </label>

        {/* Link copy / share — or 3-friend specific invites */}
        {link && useSpecific && (
          <SpecificInvites
            link={link}
            inviterName={userName}
            inviterId={userId}
            lang={lang}
            onAllInvited={() => setAllInvited(true)}
          />
        )}
        {link && !useSpecific && (
          <div style={{ marginBottom:12 }}>
            {typeof navigator !== 'undefined' && navigator.share ? (
              <button onClick={handleNativeShare} style={{
                width:'100%', padding:'13px', background:Gx.gold, border:'none', borderRadius:8,
                color:'#0A0A0A', fontFamily:Gx.sans, fontWeight:700, fontSize:14, cursor:'pointer', marginBottom:8,
              }}>
                {fr ? 'Partager mon lien →' : 'Share my link →'}
              </button>
            ) : null}
            <button onClick={handleCopy} style={{
              width:'100%', padding:'11px', background: copied ? 'rgba(78,201,148,0.1)' : 'transparent',
              border:`1px solid ${copied ? 'rgba(78,201,148,0.35)' : Gx.border}`,
              borderRadius:8, color: copied ? Gx.green : Gx.muted,
              fontFamily:Gx.sans, fontSize:13, cursor:'pointer', transition:'all 0.2s',
            }}>
              {copied
                ? (fr ? '✓ Lien copié !' : '✓ Link copied!')
                : (fr ? 'Copier mon lien' : 'Copy my link')}
            </button>
          </div>
        )}

        <button
          onClick={handleDone}
          disabled={!committed}
          style={{
            width:'100%', padding:'12px', marginTop:4,
            background: committed ? Gx.gold : 'transparent',
            border: committed ? 'none' : `1px solid ${Gx.border}`,
            borderRadius:8,
            color: committed ? '#0A0A0A' : 'rgba(255,255,255,0.3)',
            fontFamily:Gx.sans, fontWeight:700, fontSize:13,
            cursor: committed ? 'pointer' : 'not-allowed',
            transition:'all 0.15s',
          }}>
          {committed
            ? (fr ? 'Je signe — entrer dans la Ruche →' : 'I commit — enter the Hive →')
            : (fr ? 'Coche l\'engagement pour continuer' : 'Tick the commitment to continue')}
        </button>

        <p style={{ textAlign:'center', fontSize:10, color:'rgba(255,255,255,0.2)', marginTop:12, lineHeight:1.5 }}>
          {fr
            ? "Ton radar de compatibilité s'active dès le 1er avis d'ami. Révélation complète à 4 avis."
            : "Your compatibility radar activates with the 1st friend rating. Full reveal at 4 ratings."}
        </p>
      </div>
    </div>
  );
}

// ── Rating reveal overlay ─────────────────────────────────────────────────
// Shown once when the user reaches 4+ friend ratings.
// The "wow moment" — first time they see how friends perceive them.
function RatingRevealOverlay({ count, quotes, onDone }) {
  const lang = getInitialLang();
  const fr   = lang === 'fr';

  const handleDone = () => {
    saveStr('beelove_rating_reveal_shown', '1');
    onDone();
  };

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(10,10,10,0.98)',
      zIndex:1000, display:'flex', flexDirection:'column', alignItems:'center',
      justifyContent:'center', padding:24, fontFamily:Gx.sans,
    }}>
      <div style={{ maxWidth:400, width:'100%', textAlign:'center' }}>
        <div style={{ fontFamily:Gx.serif, fontSize:56, color:Gx.gold, lineHeight:1, marginBottom:16 }}>✦</div>

        <h2 style={{ fontFamily:Gx.serif, fontSize:26, fontWeight:400, color:Gx.white, marginBottom:8, lineHeight:1.3 }}>
          {fr
            ? `${count} ami${count > 1 ? 's' : ''} t'ont décrit.`
            : `${count} friend${count > 1 ? 's' : ''} described you.`}
        </h2>
        <p style={{ fontSize:13, color:Gx.muted, lineHeight:1.7, marginBottom:24 }}>
          {fr
            ? 'Ton profil s\'enrichit de leurs réponses. Découvre comment tu apparais aux autres.'
            : 'Your profile is now shaped by their answers. See how you appear to others.'}
        </p>

        {/* Quotes */}
        {quotes.length > 0 && (
          <div style={{ marginBottom:24 }}>
            {quotes.map((q, i) => (
              <div key={i} style={{
                background:Gx.card, border:`1px solid ${Gx.border}`,
                borderRadius:10, padding:'12px 16px', marginBottom:8, textAlign:'left',
              }}>
                <p style={{ fontFamily:Gx.serif, fontSize:15, color:Gx.white, margin:'0 0 4px', lineHeight:1.5, fontStyle:'italic' }}>
                  "{q.quote}"
                </p>
                {q.rater_name && q.rater_name !== 'Anonymous' && (
                  <p style={{ fontSize:11, color:Gx.muted, margin:0 }}>— {q.rater_name}</p>
                )}
              </div>
            ))}
          </div>
        )}

        <button onClick={() => {
          handleDone();
          import('./utils/navigate').then(({ navigate }) => navigate('profile'));
        }} style={{
          width:'100%', padding:'13px', background:Gx.gold, border:'none', borderRadius:8,
          color:'#0A0A0A', fontFamily:Gx.sans, fontWeight:700, fontSize:14, cursor:'pointer', marginBottom:10,
        }}>
          {fr ? 'Voir mon profil complet →' : 'See my full profile →'}
        </button>
        <button onClick={handleDone} style={{
          width:'100%', padding:'10px', background:'transparent', border:'none',
          color:Gx.muted, fontFamily:Gx.sans, fontSize:12, cursor:'pointer',
        }}>
          {fr ? 'Plus tard' : 'Later'}
        </button>
      </div>
    </div>
  );
}

const APP_PAGES = ['discover','matches','messages','beehive','profile','notifications','admin','events','agenda','upgrade','beewise','cml','scout','beeconnect'];
const STATIC_PAGES = ['about','blog','press','careers','privacy','terms','cookies','contact','how','pricing','help','safety'];

function EnvBanner() {
  const env = process.env.REACT_APP_ENV || 'development';
  if (env === 'production') return null;
  const isDev = env !== 'qa';
  const useMock = process.env.REACT_APP_USE_MOCK_DATA === 'true';
  return (
    <div className={`env-banner ${isDev ? 'env-dev' : 'env-qa'}`} style={{ fontFamily:'Outfit' }}>
      {isDev
        ? `DEV${useMock ? ' · 50 mock users loaded' : ' · live Supabase'}`
        : `QA${useMock ? ' · Mock data active' : ' · live Supabase'}`}
    </div>
  );
}

// ── Global toast listener — receives 'beelove-toast' CustomEvents from anywhere ─
function ToastHost() {
  const [toast, setToast] = useState(null);
  useEffect(() => {
    const onToast = (e) => {
      const { title, body, onClick } = e.detail || {};
      if (!title && !body) return;
      setToast({ title, body, onClick, id: Date.now() });
      setTimeout(() => setToast(t => (t && t.id === e.detail?.__id ? null : t)), 4000);
    };
    window.addEventListener('beelove-toast', onToast);
    return () => window.removeEventListener('beelove-toast', onToast);
  }, []);
  // Auto-dismiss after 4s using the id-based timeout above; use a simple timer here:
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast?.id]);
  if (!toast) return null;
  return (
    <div
      onClick={() => { toast.onClick?.(); setToast(null); }}
      style={{
        position:'fixed', top:64, left:'50%', transform:'translateX(-50%)',
        zIndex:10000, maxWidth:'92vw', minWidth:240,
        background:'#161616', border:'1px solid rgba(201,168,76,0.35)',
        borderRadius:8, padding:'10px 14px',
        fontFamily:"'Outfit',system-ui,sans-serif", color:'#F2E8D0',
        boxShadow:'0 10px 30px rgba(0,0,0,0.5)',
        cursor: toast.onClick ? 'pointer' : 'default',
      }}>
      {toast.title && <div style={{ fontSize:13, fontWeight:600, marginBottom: toast.body ? 2 : 0 }}>{toast.title}</div>}
      {toast.body && <div style={{ fontSize:12, color:'#888' }}>{toast.body}</div>}
    </div>
  );
}

// ── Path-based router ─────────────────────────────────────────────
// URL: montreal.beelove.me/discover  →  page = 'discover'
//      montreal.beelove.me/           →  screen = 'landing'
//      montreal.beelove.me/auth       →  screen = 'auth'
function getPathPage() {
  const path = window.location.pathname.replace(/^\//, '').toLowerCase().trim();
  return path || 'landing';
}

function AppInner() {
  const {setPage, page, setCurrentUser, saveQuizAnswers, setActiveNetwork, lang, setLang,
    authUser, currentUser, loading: authLoading, setSpotifyData, features} = useApp();

  const [showInterstitiel,  setShowInterstitiel]  = useState(false);
  const [showRatingReveal,  setShowRatingReveal]  = useState(false);
  const [ratingRevealData,  setRatingRevealData]  = useState(null);

  const [rateUserId, setRateUserId] = useState(() => {
    const h = getPathPage();
    if (h.startsWith('rate/')) return h.slice(5).split('?')[0];
    return null;
  });
  const [screen, setScreen]   = useState(() => {
    const h = getPathPage();
    if (h === 'auth') return 'auth';
    if (h.startsWith('rate/')) return 'rate';
    if (h === 'cml-leaderboard' || h === 'cml/leaderboard') return 'cml-leaderboard';
    if (h.startsWith('invite/')) {
      const uuid = h.slice(7);
      if (uuid) sessionStorage.setItem('pendingInviteCode', uuid);
      return 'auth';
    }
    if (APP_PAGES.includes(h)) return 'app';
    if (STATIC_PAGES.includes(h)) return 'static';
    return 'landing';
  });
  const [staticPage, setStaticPage] = useState('');

  const envH = process.env.REACT_APP_ENV !== 'production' ? 26 : 0;

  // ── Sync path → state ──────────────────────────────────────────
  const navigate = useCallback((target) => {
    const clean = target.startsWith('/') ? target : '/' + target;
    window.history.pushState({}, '', clean);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  
  // Auto-navigate based on Supabase auth state
  useEffect(() => {
    if (authLoading) return;
    if (authUser && (screen === 'auth' || screen === 'landing')) {
      setScreen('app');
      // New users (magic link / OAuth) won't have gender set — send to profile
      const isNewUser = !currentUser?.gender;
      navigate(isNewUser ? 'profile' : 'discover');
    }
    if (!authUser && screen === 'app') {
      setScreen('auth');
    }
  }, [authUser, authLoading, screen]);

useEffect(() => {
    const onNav = () => {
      const h = getPathPage();
      if (h === 'landing' || h === '') { setScreen('landing'); }
      else if (h === 'auth')           { setScreen('auth'); }
      else if (h.startsWith('spotify/callback')) {
        // Spotify OAuth PKCE callback — query string: /spotify/callback?code=...&state=...
        import('./components/SpotifyConnect').then(({ handleSpotifyCallback }) => {
          handleSpotifyCallback(window.location.search).then(({ spotifyData }) => {
            if (spotifyData && setSpotifyData) setSpotifyData(spotifyData);
            setScreen('app');
            navigate('profile');
          });
        });
      }
      else if (h === 'auth/callback') {
        // OAuth provider redirected here — session already set by Supabase
        import('./utils/supabaseAuth').then(({ handleOAuthCallback }) => {
          handleOAuthCallback().then(({ user, isNew }) => {
            if (user) {
              setScreen('app');
              navigate(isNew ? 'profile' : 'discover');
            } else {
              setScreen('auth');
            }
          });
        });
      }
      else if (h.startsWith('invite/')) {
        const uuid = h.slice(7);
        if (uuid) sessionStorage.setItem('pendingInviteCode', uuid);
        setScreen('auth');
        navigate('auth');
      }
      else if (h.startsWith('rate/'))  { setScreen('rate'); setRateUserId(h.slice(5).split('?')[0]); }
      else if (h === 'cml-leaderboard' || h === 'cml/leaderboard') { setScreen('cml-leaderboard'); }
      else if (APP_PAGES.includes(h))  { setScreen('app'); setPage(h); }
      else if (STATIC_PAGES.includes(h)) { setScreen('static'); setStaticPage(h); }
    };
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, [setPage]);

  // ── Keep pathname in sync when page changes inside app ────────────
  useEffect(() => {
    if (screen === 'app' && page) {
      if (window.location.pathname !== `/${page}`) {
        window.history.pushState(null, '', `/${page}`);
      }
    }
  }, [screen, page]);

  // handleLogin is called after successful Supabase auth
  // AppContext's subscribeAuthState() handles the actual user loading
  // This just navigates to the app — currentUser is set by AppContext
  // Check for 4+ ratings and show reveal once
  useEffect(() => {
    if (!authUser || screen !== 'app') return;
    if (features?.friend_rating === false) return;
    if (loadStr('beelove_rating_reveal_shown', '')) return;
    import('./utils/supabase').then(({ supabase }) => {
      supabase?.from('friend_ratings')
        .select('rater_name, quote')
        .eq('target_id', authUser.id)
        .limit(20)
        .then(({ data }) => {
          if ((data?.length || 0) >= 4) {
            const quotes = (data || []).filter(r => r.quote).slice(0, 2);
            setRatingRevealData({ count: data.length, quotes });
            setShowRatingReveal(true);
          }
        });
    });
  }, [authUser?.id, screen, features?.friend_rating]);

  const handleLogin = () => {
    const rateTarget = sessionStorage.getItem('pendingRateTarget');
    if (rateTarget) {
      sessionStorage.removeItem('pendingRateTarget');
      setRateUserId(rateTarget);
      setScreen('rate');
      navigate(`rate/${rateTarget}`);
      return;
    }
    // First-time users see the onboarding contract screen — but only if friend
    // rating is enabled, since the whole pact is built around that mechanic.
    if (features?.friend_rating !== false && !loadStr('beelove_contract_shown', '')) {
      setScreen('app');
      navigate('discover');
      setShowInterstitiel(true);
      return;
    }
    setScreen('app');
    navigate('discover');
  };

  if (screen === 'rate') {
    return (
      <AppProvider>
        <FriendRating token={rateUserId} lang={getInitialLang()} />
      </AppProvider>
    );
  }

  if (screen === 'cml-leaderboard') {
    return <CmlLeaderboard/>;
  }

  if (screen === 'landing') {
    return <Landing onGetStarted={() => navigate('auth')} />;
  }

  if (screen === 'auth') {
    return (
      <>
        <EnvBanner />
        <div style={{ paddingTop: envH, minHeight:'100vh', background:'var(--black)', display:'flex', flexDirection:'column', alignItems:'center' }}>
          <div className="app-container" style={{ flex:1, display:'flex', flexDirection:'column' }}>
            <Auth onLogin={handleLogin} />
          </div>
        </div>
      </>
    );
  }

  if (screen === 'static') {
    return (
      <>
        <EnvBanner />
        <div style={{ paddingTop: envH, minHeight:'100vh', background:'var(--black)' }}>
          <Navbar />
          <main className="page">
            <div className="app-container">
              <StaticPage id={staticPage} onNavigate={navigate} />
            </div>
          </main>
        </div>
      </>
    );
  }

  const renderPage = () => {
    switch(page) {
      case 'discover':      return <Discover/>;
      case 'matches':       return <Matches/>;
      case 'messages':      return <Messages/>;
      case 'profile':       return <Profile/>;
      case 'notifications': return <Notifications/>;
      case 'beehive':       return <BeeHive/>;
      case 'admin':         return <Admin/>;
      case 'cml':           return <CMLDashboard/>;
      case 'scout':          return <Scout/>;
      case 'beeconnect':     return <BeeConnect/>;
      case 'agenda':
      case 'events':        return <Agenda/>;
      case 'upgrade':       return <Upgrade onClose={() => window.history.back()}/>
      case 'beewise':       return <BeeWise/>;
      default:              return <Discover/>;
    }
  };

  // Admin gets its own full-screen layout (no navbar)
  if (page === 'admin') {
    return <><EnvBanner /><div style={{ paddingTop: envH }}>{renderPage()}</div></>;
  }
  if (page === 'cml') { return <CMLDashboard />; }

  return (
    <div style={{ minHeight:'100vh', background:'var(--black)' }}>
      <EnvBanner />
      <div style={{ paddingTop: envH }}>
        <Navbar onNavigate={navigate} />
        <main className="page">
          <div className="app-container">
            {renderPage()}
          </div>
        </main>
      </div>
      <ToastHost />
      {showInterstitiel && (
        <OnboardingInterstitiel onDone={() => setShowInterstitiel(false)} />
      )}
      {showRatingReveal && ratingRevealData && (
        <RatingRevealOverlay
          count={ratingRevealData.count}
          quotes={ratingRevealData.quotes}
          onDone={() => setShowRatingReveal(false)}
        />
      )}
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <ErrorBoundary>
          <AppInner />
        </ErrorBoundary>
      </AppProvider>
    </ErrorBoundary>
  );
}
